// @ts-nocheck
/**
 * Community slider
 * Swiper driven carousel of UGC cards (image or video) rendered from a
 * product metaobject list metafield. See sections/community-slider.liquid.
 *
 * The row always loops with the active card centred, so partial cards bleed
 * off both edges and the row never ends in empty space. The section repeats
 * the metaobject entries until there are enough slides to sustain that.
 *
 * Only the active, centred card ever plays. Every other card stays paused and
 * parked at its first frame, and starts from the top when it reaches the
 * centre — so the row decodes one video at a time instead of every card on
 * screen. An IntersectionObserver on the section gates that on the row being
 * in view at all. Audio is exclusive: unmuting one card mutes the others.
 *
 * Tapping a card's media opens a lightbox. The section renders one <dialog>
 * plus one inert <template> per original post; opening clones the matching
 * template in, closing throws the markup away again so the video stops and
 * releases what it buffered.
 */
/* Slides Swiper keeps in hand on each side of the visible row. */
const LOOP_BUFFER = 4;
/* Hard ceiling so a stray card width can never balloon the DOM. Repeats reuse
   the same media URLs, so extra slides cost DOM, not bandwidth. */
const MAX_SLIDES = 60;
/* Pointer travel, in px, past which a click is the end of a drag, not a tap. */
const DRAG_SLOP = 10;

class CommunitySlider extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.videoObserver = null;
    this.initialized = false;
    /* Whether the row itself is on screen. The centred card only plays while
       the section is in view. */
    this.sectionVisible = false;
    /* Audio preference for the row, set by a card's mute toggle, so the sound
       carries on to the next card the shopper swipes to instead of needing
       the toggle on every one. */
    this.audioOn = false;
    this.pointerStart = null;
    this.lastTrigger = null;
    /* Cards that fit across the row, measured before Swiper starts. */
    this.perView = 0;
    this.onClick = this.onClick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onHostScroll = this.onHostScroll.bind(this);
    this.onModalClick = this.onModalClick.bind(this);
    this.onModalClose = this.onModalClose.bind(this);
  }

  /* The row is clipped with `overflow: clip`, which cannot scroll — but
     Safari below 16 falls back to `hidden`, which can. Anything that reveals
     a focused card (or the lightbox, a DOM descendant) would then scroll the
     carousel sideways and leave it parked off its own translate. Swiper
     guards its wrapper the same way; this covers the section box. */
  onHostScroll() {
    if (this.scrollLeft !== 0) this.scrollLeft = 0;
    if (this.scrollTop !== 0) this.scrollTop = 0;
  }

  connectedCallback() {
    this.whenSwiperReady().then(() => {
      // The element can be moved or removed while the poll above is pending,
      // and the theme editor reconnects the same node when a section moves.
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;

      // Swiper reorders the slide elements to build the loop, so videos are
      // wired up afterwards and the observer holds the final nodes.
      this.initSwiper();
      this.initVideos();
      this.initModal();

      this.addEventListener('pointerdown', this.onPointerDown, true);
      this.addEventListener('scroll', this.onHostScroll);
      /* Capture phase: Swiper listens for clicks on a descendant, so this has
         to run first for a mute tap or a card tap to avoid also re-centring
         the card. */
      this.addEventListener('click', this.onClick, true);
    });
  }

  disconnectedCallback() {
    if (this.modalOpen) this.closeModal();
    document.documentElement.removeAttribute('scroll-lock');

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }

    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }

    this.sectionVisible = false;

    const dialog = this.dialog;
    if (dialog) {
      dialog.removeEventListener('click', this.onModalClick);
      dialog.removeEventListener('close', this.onModalClose);
    }

    this.removeEventListener('scroll', this.onHostScroll);
    this.removeEventListener('pointerdown', this.onPointerDown, true);
    this.removeEventListener('click', this.onClick, true);
    this.initialized = false;
  }

  /* Swiper is loaded with `defer` from the layout, so it is normally ready by
     the time this element upgrades. Poll briefly for the edge cases. */
  whenSwiperReady() {
    if (window.Swiper) return Promise.resolve();

    return new Promise((resolve) => {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (window.Swiper || attempts > 100) {
          clearInterval(timer);
          if (!window.Swiper) {
            console.warn('[community-slider] Swiper never loaded — slider left static.');
          }
          resolve();
        }
      }, 50);
    });
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && !this.reducedMotion;
  }

  /* Every video in the section, the lightbox included — mute exclusivity has
     to span both so a card and the open post never play over each other. */
  get videos() {
    return Array.from(this.querySelectorAll('.community-card__media video'));
  }

  get sliderVideos() {
    return Array.from(this.querySelectorAll('.swiper-wrapper .community-card__media video'));
  }

  /* The centred card — the only one that plays. Swiper stamps
     `swiper-slide-active` on it, but read it off the instance instead: the
     class lands a tick later, and a slide change has to be acted on at once.
     With no Swiper at all the row is static, so the first card stands in.

     `afterInit` runs inside the Swiper constructor, before `this.swiperInstance`
     has been assigned, so callbacks hand their own instance in rather than
     letting this fall back to the first card in the DOM. */
  activeSlideOf(swiper = this.swiperInstance) {
    if (swiper && swiper.slides && swiper.slides.length) {
      return swiper.slides[swiper.activeIndex] || null;
    }

    return this.querySelector('.community-slider__slide');
  }

  get activeSlide() {
    return this.activeSlideOf();
  }

  get dialog() {
    return this.querySelector('[data-community-modal]');
  }

  get modalContent() {
    return this.querySelector('[data-community-modal-content]');
  }

  get modalOpen() {
    const dialog = this.dialog;
    return Boolean(dialog && dialog.open);
  }

  /* Swiper 11 silently turns the loop off when there are not clearly more
     slides than fit on screen, which is what left a gap after the last card.
     The section already repeats the entries, but a very wide viewport (or a
     narrow card width) can still outrun it — top the row up here. */
  /* How many distinct entries the row is built from — the metafield posts plus
     the section's blocks. Every slide carries the index of the entry it came
     from, so the highest one plus one is the count. */
  countEntries(slides) {
    let highest = -1;

    slides.forEach((slide) => {
      const index = parseInt(slide.dataset.postIndex, 10);
      if (!Number.isNaN(index) && index > highest) highest = index;
    });

    return highest + 1;
  }

  /* Which slide to open on. The design calls for the first entry — the first
     post in the metafield's own order — to be the active, centred card. Slide
     0 would do that, but it is the left end of the row: Swiper would have
     nothing buffered before it and the row would open against empty space.
     Every repeat of entry 0 is the same card, so open on whichever copy sits
     nearest the middle and get both. */
  openingSlideIndex(slides) {
    const middle = Math.floor(slides.length / 2);
    let best = middle;
    let bestDistance = Infinity;

    slides.forEach((slide, index) => {
      if (slide.dataset.postIndex !== '0') return;

      const distance = Math.abs(index - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  ensureEnoughSlides(swiperEl) {
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return 0;

    const originals = Array.from(wrapper.children);
    if (originals.length === 0) return 0;

    const slideWidth = originals[0].getBoundingClientRect().width;
    const rowWidth = swiperEl.getBoundingClientRect().width;
    if (!slideWidth || !rowWidth) return originals.length;

    /* Swiper buffers `loopedSlides` cards on each side and, every time the row
       settles, prepends when `activeIndex < loopedSlides` or appends when
       `activeIndex > slides.length - loopedSlides * 2`. Those two windows
       overlap unless the row is comfortably longer than `loopedSlides * 2`,
       and in the overlap it prepends and appends in turn and settles one card
       short of the left edge. Keeping the row at three times the buffer
       leaves a wide band in the middle where neither fires. */
    const perView = Math.ceil(rowWidth / slideWidth) + 2;
    const needed = (perView + LOOP_BUFFER) * 3;

    // Kept for the loop buffer and the opening position below.
    this.perView = perView;

    if (needed > MAX_SLIDES) {
      console.warn(
        `[community-slider] ${needed} slides needed to loop but capped at ${MAX_SLIDES}; the row may end in a gap.`,
      );
    }

    /* Clone from the first run of entries rather than from the whole row, so
       the cycle carries on unbroken past the end of what the section rendered.
       Sourcing by position in the row instead would repeat a card next to
       itself wherever the row length is not a multiple of the entry count. */
    const entryCount = this.countEntries(originals) || originals.length;

    for (let i = originals.length; i < needed && i < MAX_SLIDES; i += 1) {
      const clone = originals[i % entryCount].cloneNode(true);
      clone.querySelectorAll('video').forEach((video) => {
        video.muted = true;
      });
      /* Clones repeat posts already in the row — keep them out of the tab
         order and the accessibility tree, as the section does for its own
         repeats. They stay clickable. */
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      wrapper.appendChild(clone);
    }

    return wrapper.children.length;
  }

  initSwiper() {
    const swiperEl = this.querySelector('.community-slider__swiper');
    if (!swiperEl || !window.Swiper) return;

    const slideCount = this.ensureEnoughSlides(swiperEl);
    const openingSlide = this.openingSlideIndex(
      Array.from(swiperEl.querySelectorAll('.swiper-wrapper > *')),
    );

    const autoplaySpeed = parseInt(this.dataset.autoplaySpeed || '5000', 10);
    const gapMobile = parseInt(this.dataset.gapMobile || '12', 10);
    const gapDesktop = parseInt(this.dataset.gapDesktop || '12', 10);

    const config = {
      slidesPerView: 'auto',
      // The design centres the active card with cards bleeding off both edges.
      centeredSlides: true,
      loop: true,
      /* Deliberately small. This adds to `loopedSlides`, and a large buffer
         against a finite row is what made the loop prepend and append in
         turn — `ensureEnoughSlides` gives the row three times this instead. */
      loopAdditionalSlides: LOOP_BUFFER,
      /* The first entry, on the copy nearest the middle of the row — see
         `openingSlideIndex`. Mid-row matters because Swiper 11's loop with
         `centeredSlides` and `slidesPerView: 'auto'` does not reliably place
         slides before the first one until the row is nudged, which left the
         active card against empty space and nothing to drag back to. */
      initialSlide: openingSlide,
      spaceBetween: gapMobile,
      breakpoints: {
        990: { spaceBetween: gapDesktop },
      },
      speed: 600,
      watchSlidesProgress: true,
      grabCursor: true,
      /* Cards open the lightbox on click, so Swiper must not also treat a tap
         as "bring this slide forward" — that fought the drag and could fire
         alongside the popup. */
      slideToClickedSlide: false,
      /* Swiper refuses to drag from anything matching `focusableElements`, so
         a press inside a text field or a <select> behaves natively. Its
         default list is "input, select, option, textarea, button, video,
         label" — and the card media is a <video> under a <button> overlay, so
         both matched.

         Two things followed from that. Swiper skipped its own
         `preventDefault()` on pointerdown, which let the browser focus the
         button and then scroll to reveal it — the row appearing to jump to
         the first slide. And `onTouchMove` returned early on every move once
         that button was the active element, so the row never translated: the
         drag was silently dead, and `allowClick` was cleared too, which left
         the media deaf to both gestures.

         Dropping `button` and `video` makes the media drag like any other
         surface and restores the `preventDefault()` that stops the focus
         scroll. `click` is not a compatibility mouse event, so it still
         fires and a tap still opens the post. */
      focusableElements: 'input, select, option, textarea, label',
      // A few px of slop before a press counts as a swipe, so taps stay taps.
      threshold: 5,
      keyboard: { enabled: true },
      /* Playback follows the centred card, so it is re-evaluated the moment
         the active slide changes — `slideChange` fires as the move starts,
         not when it lands, so the old card goes quiet immediately. `loopFix`
         covers the loop reshuffling the row under us, which moves
         `activeIndex` onto a different node for the same card. */
      on: {
        afterInit: (swiper) => this.syncActiveVideo(swiper),
        slideChange: (swiper) => this.syncActiveVideo(swiper),
        loopFix: (swiper) => this.syncActiveVideo(swiper),
      },
      navigation: {
        prevEl: this.querySelector('.community-slider__arrow--prev'),
        nextEl: this.querySelector('.community-slider__arrow--next'),
        // Loop mode never disables the arrows, but keep them off the default
        // Swiper class so nothing else can grey them out.
        disabledClass: 'community-slider__arrow--disabled',
      },
      a11y: {
        prevSlideMessage: 'Previous story',
        nextSlideMessage: 'Next story',
      },
    };

    if (this.autoplayEnabled) {
      config.autoplay = { delay: autoplaySpeed, disableOnInteraction: false };
    }

    this.swiperInstance = new window.Swiper(swiperEl, config);

    /* Swiper drops loop mode silently when the row is too short for what it
       needs, and the only symptom is a gap at one end. Surface the numbers —
       behind a guard, so a diagnostic can never take the slider down. */
    try {
      const perViewDynamic = this.swiperInstance.slidesPerViewDynamic();
      const loopedSlides = perViewDynamic + config.loopAdditionalSlides;
      /* The prepend and append windows must not overlap, or the row settles
         one card short of an edge. See `ensureEnoughSlides`. */
      const required = loopedSlides * 2 + perViewDynamic;
      if (slideCount < required) {
        console.warn(
          `[community-slider] ${slideCount} slides, but ${required} are needed for a clean loop ` +
            `(buffer ${loopedSlides} x2 + ${perViewDynamic} per view). The row will fall short at one edge.`,
        );
      }
    } catch (error) {
      /* Diagnostic only. */
    }
  }

  /* Bring a card to the centre. The clicked card becomes the active one, as
     it would have with `slideToClickedSlide` — but driven from here, so it
     cannot race the lightbox opening on the same tap. */
  centerSlide(slide) {
    const swiper = this.swiperInstance;
    if (!swiper) return;

    // Swiper stamps this on every slide once the loop is built.
    const realIndex = parseInt(slide.getAttribute('data-swiper-slide-index'), 10);
    if (!Number.isNaN(realIndex)) {
      if (realIndex !== swiper.realIndex) swiper.slideToLoop(realIndex);
      return;
    }

    // No loop in play — fall back to the card's position in the row.
    const index = Array.prototype.indexOf.call(swiper.slides, slide);
    if (index >= 0 && index !== swiper.activeIndex) swiper.slideTo(index);
  }

  initVideos() {
    const videos = this.sliderVideos;
    if (videos.length === 0) return;

    if (this.reducedMotion) {
      videos.forEach((video) => video.pause());
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.sectionVisible = true;
      this.syncActiveVideo();
      return;
    }

    /* One observer on the section, not one per video: every card but the
       centred one is paused regardless, so the only thing left to watch is
       whether the row is on screen at all. */
    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.sectionVisible = entry.isIntersecting;
        });
        this.syncActiveVideo();
      },
      { threshold: 0.1 },
    );

    this.videoObserver.observe(this);
  }

  /* Exactly one video runs at a time: the one in the centred card. Every
     other card is paused and wound back to its first frame, so it starts
     from the top when it reaches the centre rather than resuming mid-clip. */
  syncActiveVideo(swiper) {
    const active = this.activeSlideOf(swiper);
    const canPlay = this.sectionVisible && !this.modalOpen && !this.reducedMotion;

    this.sliderVideos.forEach((video) => {
      if (canPlay && active && active.contains(video)) {
        // The row's audio choice, not the card's — see `audioOn`.
        video.muted = !this.audioOn;
        this.syncMuteButton(video);
        if (video.paused) this.play(video);
        return;
      }

      if (!video.paused) video.pause();

      if (!video.muted) {
        video.muted = true;
        this.syncMuteButton(video);
      }

      /* Can throw while the video has no loaded range to seek in — it is
         already at zero in that case, so there is nothing to do. */
      if (video.currentTime !== 0) {
        try {
          video.currentTime = 0;
        } catch (error) {
          /* Ignore. */
        }
      }
    });
  }

  initModal() {
    const dialog = this.dialog;
    if (!dialog) return;

    dialog.addEventListener('click', this.onModalClick);
    // Covers the close button, the backdrop and the Escape key alike.
    dialog.addEventListener('close', this.onModalClose);
  }

  play(video) {
    const playback = video.play();
    // A pending unmuted play can be refused; fall back to muted playback.
    if (playback && typeof playback.catch === 'function') {
      playback.catch(() => {
        /* The browser will not play this unmuted, so it will not play any of
           them unmuted — drop the row's preference rather than have every
           card fall back one at a time. */
        this.audioOn = false;
        video.muted = true;
        this.syncMuteButton(video);
        video.play().catch(() => {});
      });
    }
  }

  onPointerDown(event) {
    this.pointerStart = { x: event.clientX, y: event.clientY };
  }

  onClick(event) {
    /* Always consume the recorded press, so a pointerdown that never produced
       a matching click cannot be measured against a later one. */
    const pressedAt = this.pointerStart;
    this.pointerStart = null;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const muteButton = target.closest('.community-card__mute');
    if (muteButton && this.contains(muteButton)) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleMute(muteButton);
      return;
    }

    const trigger = target.closest('[data-community-open]');
    const slide = trigger && trigger.closest('.community-slider__slide');
    if (!trigger || !slide || !this.contains(trigger)) return;

    /* A click that ends a swipe is not a tap. Swiper suppresses those itself,
       but only on its own container — this listener runs on an ancestor in
       the capture phase, so it has to decide for itself.

       `allowClick` is the same flag Swiper uses for that, and it is false
       from the moment a press turns into a drag; the travel check behind it
       covers the case where Swiper never loaded. Keyboard activation goes
       through neither, so there is nothing to measure and the popup opens. */
    if (this.swiperInstance && this.swiperInstance.allowClick === false) return;

    if (pressedAt) {
      const travel = Math.hypot(event.clientX - pressedAt.x, event.clientY - pressedAt.y);
      if (travel > DRAG_SLOP) return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Centre the card first, then open it — both, on the one tap.
    this.centerSlide(slide);
    this.openModal(slide, trigger);
  }

  openModal(slide, trigger) {
    const dialog = this.dialog;
    const content = this.modalContent;
    const index = slide.dataset.postIndex;
    if (!dialog || !content || index == null || index === '') return;
    // `showModal()` throws on an already-open dialog.
    if (dialog.open) return;

    const template = this.querySelector(`template[data-community-post="${index}"]`);
    if (!template) return;

    content.replaceChildren(template.content.cloneNode(true));
    dialog.setAttribute('aria-label', template.dataset.handle || 'Community post');

    this.lastTrigger = trigger || null;

    /* The row would otherwise keep playing, and advancing, behind the overlay. */
    this.sliderVideos.forEach((video) => video.pause());
    if (this.swiperInstance && this.swiperInstance.autoplay) {
      this.swiperInstance.autoplay.stop();
    }

    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');

    const video = content.querySelector('video');
    if (video) {
      // Posts open muted, as the cards do; the toggle is there to unmute.
      video.muted = true;
      this.syncMuteButton(video);
      if (!this.reducedMotion) this.play(video);
    }

    const closeButton = content.querySelector('[data-community-modal-close]');
    if (closeButton) {
      /* Without `preventScroll` the browser scrolls ancestors to reveal the
         focused node — and the dialog sits inside the clipped carousel. */
      closeButton.focus({ preventScroll: true });
    }
  }

  closeModal() {
    const dialog = this.dialog;
    if (dialog && dialog.open) dialog.close();
  }

  onModalClick(event) {
    const target = event.target instanceof Element ? event.target : null;

    if (target && target.closest('[data-community-modal-close]')) {
      this.closeModal();
      return;
    }

    /* Enter on a link or button inside the panel reports no coordinates, so
       the hit test below would read it as a click on the backdrop. Touch taps
       do not always set `detail`, so test the coordinates themselves. */
    if (event.clientX === 0 && event.clientY === 0) return;

    const panel = this.dialog.querySelector('.community-modal__inner');
    if (!panel) return;

    /* `showModal()` makes the dialog itself the full viewport, so anything
       landing outside the panel box is a click on the backdrop. */
    const { left, right, top, bottom } = panel.getBoundingClientRect();
    const outside =
      event.clientX < left || event.clientX > right || event.clientY < top || event.clientY > bottom;

    if (outside) this.closeModal();
  }

  onModalClose() {
    document.documentElement.removeAttribute('scroll-lock');

    /* Dropping the markup stops the video and releases what it buffered. */
    const content = this.modalContent;
    if (content) content.replaceChildren();

    if (this.autoplayEnabled && this.swiperInstance && this.swiperInstance.autoplay) {
      this.swiperInstance.autoplay.start();
    }

    /* The observer will not fire again — nothing moved — so put the centred
       card back to playing by hand. */
    this.syncActiveVideo();

    if (this.lastTrigger && this.lastTrigger.isConnected) {
      this.lastTrigger.focus({ preventScroll: true });
    }
    this.lastTrigger = null;
  }

  toggleMute(button) {
    const media = button.closest('.community-card__media');
    const video = media ? media.querySelector('video') : null;
    if (!video) return;

    const shouldUnmute = video.muted;

    // Audio is exclusive — only one card can be heard at a time.
    this.videos.forEach((other) => {
      if (other === video) return;
      other.muted = true;
      this.syncMuteButton(other);
    });

    video.muted = !shouldUnmute;
    this.syncMuteButton(video);

    /* A card's toggle sets the preference for the whole row, so the sound
       carries to the next centred card. The lightbox keeps its own: it always
       opens muted. */
    if (button.closest('.community-slider__slide')) this.audioOn = shouldUnmute;

    if (shouldUnmute && video.paused) this.play(video);
  }

  syncMuteButton(video) {
    const media = video.closest('.community-card__media');
    const button = media ? media.querySelector('.community-card__mute') : null;
    if (!button) return;

    button.classList.toggle('community-card__mute--muted', video.muted);
    button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  }
}

if (!customElements.get('community-slider')) {
  customElements.define('community-slider', CommunitySlider);
}
